import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import moment from 'moment';
import { RootState } from '.';
import { getAttendanceClockingHistory, getLabourClockingHistory } from '../api/dms/clockingHistory.api';
import { logConsole, logTransaction } from '../common/log';
import { getStateDateAsString, getStateStringAsDate, getStateStringAsMoment, handleThunkError } from '../common/util';
import { ClockingDirection, ClockingType, IAttendanceClocking, IClockingHistory, ILabourClocking } from '../models/dms/clocking.model';
import { IClockingRequestQueueItem } from '../models/dms/requestQueue.model';
import { LogType } from '../models/log.model';
import { ConfigName, updateConfigObject } from '../sqlite/common/config';
import { setIsLoadingCountState } from './appSlice';

interface IClockingHistoryState {
  selectedClockingHistory: Record<string, IClockingHistory[]>;
  selectedClockingHitoryLoading: boolean;
}

const initialState: IClockingHistoryState = {
  selectedClockingHistory: {},
  selectedClockingHitoryLoading: false,
};

const sortClockingHistory = (data: IClockingHistory[]): IClockingHistory[] => {
  return data.sort((a, b) => {
    const dateA = moment(a.clockingDateTime);
    const dateB = moment(b.clockingDateTime);

    // Primary sorting: by clockingDateTime
    if (dateA.isBefore(dateB)) return -1;
    if (dateA.isAfter(dateB)) return 1;

    // Secondary sorting for identical date/time:
    // - Attendance ON before Labour ON
    // - Labour OFF before Attendance OFF
    if (a.direction === ClockingDirection.On && b.direction === ClockingDirection.On) {
      if (a.type === ClockingType.Attendance && b.type === ClockingType.Labour) {
        return -1; // Attendance ON before Labour ON
      }
      if (a.type === ClockingType.Labour && b.type === ClockingType.Attendance) {
        return 1; // Labour ON after Attendance ON
      }
    }

    // Special case for both being "OFF" at the same time: Labour OFF comes before Attendance OFF
    if (a.direction === ClockingDirection.Off && b.direction === ClockingDirection.Off) {
      if (a.type === ClockingType.Labour && b.type === ClockingType.Attendance) {
        return -1; // Labour OFF before Attendance OFF
      }
      if (a.type === ClockingType.Attendance && b.type === ClockingType.Labour) {
        return 1; // Attendance OFF after Labour OFF
      }
    }

    // Sorting by direction (ON before OFF)
    if (a.direction !== b.direction) {
      return a.direction - b.direction; // ON (1) comes before OFF (0)
    }

    // If same direction, we fall back to type sorting (Attendance before Labour in tie)
    return a.type - b.type;
  });
};

const groupByClockingDateTime = (data: IClockingHistory[]): Record<string, IClockingHistory[]> => {
  return data.reduce((groupedData, currentEntry) => {
    const clockingTime = currentEntry.clockingDateTime;
    if (!groupedData[clockingTime]) {
      groupedData[clockingTime] = [];
    }
    groupedData[clockingTime].push(currentEntry);
    return groupedData;
  }, {} as Record<string, IClockingHistory[]>);
};

const ungroupClockingData = (groupedData: Record<string, IClockingHistory[]>): IClockingHistory[] => {
  const ungroupedArray: IClockingHistory[] = [];

  // Iterate over the keys (which are the timestamps)
  for (const clockingTime in groupedData) {
    if (groupedData.hasOwnProperty(clockingTime)) {
      // Push all entries in the array back into the ungroupedArray
      ungroupedArray.push(...groupedData[clockingTime]);
    }
  }

  // Return the ungrouped array
  return ungroupedArray;
};

/// THUNKS

export const setSelectedClockingHistoryStoreAsync = createAsyncThunk<{}, Record<string, IClockingHistory[]>>('clockingHistory/setSelectedClockingHistoryStoreAsync', async (clockingHistory, { rejectWithValue, dispatch }) => {
  logTransaction(LogType.Info, `Setting selected clocking history to storage`, '[clockingHistory] setSelectedClockingHistoryStoreAsync');
  try {
    dispatch(setSelectedClockingHistoryState(clockingHistory));
    await updateConfigObject<Record<string, IClockingHistory[]>>(ConfigName.SelectedClockingHistory, clockingHistory, false);
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const fetchClockingHistoryStoreAsync = createAsyncThunk<{}, { techCode: string; clockingDate: string }>('clockingHistory/fetchClockingHistoryStoreAsync', async (payload, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, `Fetching clocking history from database - clockingDate: ${payload.clockingDate}`, '[clockingHistory] fetchClockingHistoryStoreAsync');
  try {
    dispatch(setIsLoadingCountState(1));
    dispatch(setSelectedClockingHistoryLoadingState(true));

    const attendanceClocking = await getAttendanceClockingHistory(payload.techCode, getStateStringAsDate(payload.clockingDate));
    const labourClocking = await getLabourClockingHistory(payload.techCode, getStateStringAsDate(payload.clockingDate));
    const queue = (getState() as RootState).requestQueue.clockingQueue || ([] as IClockingRequestQueueItem[]);

    const queuedAttendance = queue
      .filter(
        (queueItem) =>
          queueItem.data.type === ClockingType.Attendance &&
          queueItem.data.technicianCode === payload.techCode &&
          (getStateStringAsMoment(queueItem.data.startDateTime).isSame(moment(payload.clockingDate), 'date') || getStateStringAsMoment(queueItem.data.endDateTime).isSame(moment(payload.clockingDate), 'date')),
      )
      .map((queue) => {
        const queueData = queue.data as IAttendanceClocking;
        return {
          type: queueData.type,
          direction: queueData.direction,
          clockingDateTime: queueData.direction === ClockingDirection.On ? queueData.startDateTime : queueData.endDateTime,
          unSync: true,
          id: queue.id,
        } as IClockingHistory;
      }) as IClockingHistory[];

    const queuedLabour = queue
      .filter(
        (queueItem) =>
          queueItem.data.type === ClockingType.Labour &&
          queueItem.data.technicianCode === payload.techCode &&
          (getStateStringAsMoment(queueItem.data.startDateTime).isSame(moment(payload.clockingDate), 'date') || getStateStringAsMoment(queueItem.data.endDateTime).isSame(moment(payload.clockingDate), 'date')),
      )
      .map((queue) => {
        const queueData = queue.data as ILabourClocking;
        return {
          type: queueData.type,
          direction: queueData.direction,
          branchCode: queueData.jobData.branchCode,
          repairOrderNumber: queueData.jobData.repairOrderNumber,
          jobType: queueData.jobData.jobType,
          jobCode: queueData.jobData.jobCode,
          clockingDateTime: queueData.direction === ClockingDirection.On ? queueData.startDateTime : queueData.endDateTime,
          unSync: true,
          id: queue.id,
        } as IClockingHistory;
      }) as IClockingHistory[];

    const attendanceData = attendanceClocking.items.reduce((val, item) => {
      // Start attenance
      val.push({
        type: ClockingType.Attendance,
        direction: ClockingDirection.On,
        clockingDateTime: getStateDateAsString(item.startDateTime),
        unSync: false,
      } as IClockingHistory);

      // Finish attendance
      if (item.endDateTime !== null) {
        val.push({
          type: ClockingType.Attendance,
          direction: ClockingDirection.Off,
          clockingDateTime: getStateDateAsString(item.endDateTime),
          unSync: false,
        } as IClockingHistory);
      }
      return val;
    }, [] as IClockingHistory[]) as IClockingHistory[];

    const labourData = labourClocking.items.reduce((val, item) => {
      // Start labour
      val.push({
        type: ClockingType.Labour,
        direction: ClockingDirection.On,
        branchCode: item.branchCode,
        repairOrderNumber: item.repairOrderNumber,
        jobType: item.jobType,
        jobCode: item.jobCode,
        clockingDateTime: getStateDateAsString(item.startDateTime),
        unSync: false,
      } as IClockingHistory);

      // Finish labour
      if (item.endDateTime !== null) {
        val.push({
          type: ClockingType.Labour,
          direction: ClockingDirection.Off,
          branchCode: item.branchCode,
          repairOrderNumber: item.repairOrderNumber,
          jobType: item.jobType,
          jobCode: item.jobCode,
          clockingDateTime: getStateDateAsString(item.endDateTime),
          unSync: false,
        } as IClockingHistory);
      }
      return val;
    }, [] as IClockingHistory[]) as IClockingHistory[];

    const sortedData = sortClockingHistory([...attendanceData, ...queuedAttendance, ...labourData, ...queuedLabour]);
    const groupedData = groupByClockingDateTime(sortedData);

    await dispatch(setSelectedClockingHistoryStoreAsync(groupedData));

    dispatch(setIsLoadingCountState(-1));
    dispatch(setSelectedClockingHistoryLoadingState(false));

    return sortedData;
  } catch (err: any) {
    await dispatch(setSelectedClockingHistoryStoreAsync({}));
    return handleThunkError(err, rejectWithValue, setIsLoadingCountState(-1), setSelectedClockingHistoryLoadingState(false));
  }
});

export const addQueueToSelectedClockingHistoryStoreAsync = createAsyncThunk<{}, { data: ILabourClocking | IAttendanceClocking; id: number }>(
  'clockingHistory/addQueueToSelectedClockingHistoryStoreAsync',
  async (payload, { rejectWithValue, dispatch, getState }) => {
    logTransaction(LogType.Info, `Adding clocking queue item to selected clocking history`, '[clockingHistory] addQueueToSelectedClockingHistoryStoreAsync');
    try {
      let currentHistory = ungroupClockingData({ ...(getState() as RootState).clockingHistory.selectedClockingHistory });
      let newClockingHistory = {
        type: payload.data.type,
        direction: payload.data.direction,
        unSync: true,
        id: payload.id,
      } as IClockingHistory;

      if (payload.data.type === ClockingType.Attendance) {
        const attendanceClocking = payload.data as IAttendanceClocking;
        const dt = payload.data.direction === ClockingDirection.On ? attendanceClocking.startDateTime : attendanceClocking.endDateTime;
        newClockingHistory = {
          ...newClockingHistory,
          clockingDateTime: dt,
        } as IClockingHistory;
      } else {
        const labourClocking = payload.data as ILabourClocking;
        const dt = payload.data.direction === ClockingDirection.On ? labourClocking.startDateTime : labourClocking.endDateTime;
        newClockingHistory = {
          ...newClockingHistory,
          branchCode: labourClocking.jobData.branchCode,
          jobCode: labourClocking.jobData.jobCode,
          jobType: labourClocking.jobData.jobType,
          repairOrderNumber: labourClocking.jobData.repairOrderNumber,
          clockingDateTime: dt,
        } as IClockingHistory;
      }

      currentHistory = [newClockingHistory, ...currentHistory];

      const sortedData = sortClockingHistory(currentHistory);
      const groupedData = groupByClockingDateTime(sortedData);

      await dispatch(setSelectedClockingHistoryStoreAsync(groupedData));
    } catch (err: any) {
      return handleThunkError(err, rejectWithValue);
    }
  },
);

export const updateQueueToSelectedClockingHistoryStoreAsync = createAsyncThunk<{}, number>('clockingHistory/updateQueueToSelectedClockingHistoryStoreAsync', async (id, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, `Updating clocking history, setting index ${id} to unSync: false`, '[clockingHistory] updateQueueToSelectedClockingHistoryStoreAsync');
  try {
    let currentHistory = ungroupClockingData({ ...(getState() as RootState).clockingHistory.selectedClockingHistory });
    const historyItemIndex = currentHistory.findIndex((hist) => hist.id === id);
    if (historyItemIndex >= 0) {
      const historyItem = {
        ...currentHistory[historyItemIndex],
        unSync: false,
      };
      currentHistory.splice(historyItemIndex, 1, historyItem);
    }

    const sortedData = sortClockingHistory(currentHistory);
    const groupedData = groupByClockingDateTime(sortedData);

    await dispatch(setSelectedClockingHistoryStoreAsync(groupedData));
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

/// SLICE

export const clockingHistorySlice = createSlice({
  name: 'clockingHistory',
  initialState,
  reducers: {
    setSelectedClockingHistoryState: (state, action: PayloadAction<Record<string, IClockingHistory[]>>) => {
      logConsole(LogType.Info, '[clockingHistory] setSelectedClockingHistoryState');
      state.selectedClockingHistory = action.payload;
    },
    setSelectedClockingHistoryLoadingState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[clockingHistory] setSelectedClockingHistoryLoadingState: ${action.payload}`);
      state.selectedClockingHitoryLoading = action.payload;
    },
  },
});

/// REDUCERS

export const { setSelectedClockingHistoryState, setSelectedClockingHistoryLoadingState } = clockingHistorySlice.actions;

/// CUSTOM SELECTORS

export default clockingHistorySlice.reducer;
